import { Component } from '@theme/component';
import { getIntersectionRoot } from '@theme/scroll-container';

/** Deferred training playlist. Sources are attached only near the viewport. */
class GbPdpTraining extends Component {
  /** @type {{player?: HTMLVideoElement}} */
  refs;
  #listeners;
  #observer;
  #index = 0;
  #near = false;
  #generation = 0;
  connectedCallback() { super.connectedCallback(); this.#initialize(); }
  updatedCallback() { super.updatedCallback(); this.#initialize(); }
  disconnectedCallback() { super.disconnectedCallback(); this.#cleanup(); }
  #cleanup() { this.#generation++; this.#listeners?.abort(); this.#observer?.disconnect(); this.refs.player?.pause(); this.#near = false; }
  #initialize() {
    this.#cleanup();
    const player = this.refs.player;
    const items = [...this.querySelectorAll('[data-training-item]')];
    if (!player || !items.length) return;
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#index = Math.min(this.#index, items.length - 1);
    const state = () => {
      const button = this.querySelector('[data-training-toggle]');
      button?.setAttribute('aria-label', player.paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      button?.setAttribute('aria-pressed', String(!player.paused));
      const icon = button?.querySelector('svg');
      if (icon) icon.innerHTML = player.paused ? '<path d="M5 3l14 9-14 9V3z"/>' : '<rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>';
    };
    const poster = () => {
      for (const [i, item] of items.entries()) item.setAttribute('aria-pressed', String(i === this.#index));
      const url = items[this.#index].querySelector('img')?.getAttribute('src');
      if (url) player.poster = url;
    };
    const play = () => {
      const generation = this.#generation;
      player.play()?.catch(() => { if (generation === this.#generation) state(); });
    };
    const load = (autoplay) => {
      if (!this.#near || motion.matches) return;
      player.pause();
      player.replaceChildren();
      let sources;
      try { sources = JSON.parse(items[this.#index].dataset.sources || '[]'); } catch { sources = []; }
      for (const source of sources || []) {
        if (!source.url) continue;
        const node = document.createElement('source'); node.src = source.url; node.type = source.mime_type || source.type || 'video/mp4'; player.append(node);
      }
      player.muted = true; player.load();
      if (autoplay && !document.hidden) play();
    };
    for (const [i, item] of items.entries()) item.addEventListener('click', () => {
      this.#index = i; poster(); load(true);
    }, { signal });
    player.controls = false;
    this.querySelector('.gb-training__controls').hidden = false;
    this.querySelector('[data-training-toggle]')?.addEventListener('click', () => {
      if (motion.matches || !this.#near) return;
      if (player.paused) { if (!player.querySelector('source')) load(false); play(); } else player.pause();
    }, { signal });
    player.addEventListener('timeupdate', () => {
      const bar = this.querySelector('.gb-training__progress span');
      if (bar) bar.style.width = `${player.duration ? Math.min(100, player.currentTime / player.duration * 100) : 0}%`;
    }, { signal });
    for (const event of ['play', 'pause']) player.addEventListener(event, state, { signal });
    player.addEventListener('ended', () => {
      if (this.dataset.advance === 'false' || motion.matches || !this.#near) return;
      this.#index = (this.#index + 1) % items.length; poster(); load(true);
    }, { signal });
    document.addEventListener('visibilitychange', () => { if (document.hidden) player.pause(); }, { signal });
    motion.addEventListener('change', () => this.#initialize(), { signal });
    poster(); state();
    if (motion.matches) { player.removeAttribute('src'); player.replaceChildren(); player.load(); return; }
    const root = getIntersectionRoot();
    this.#observer = new IntersectionObserver(entries => {
      this.#near = entries.some(e => e.isIntersecting);
      if (this.#near) { if (!player.querySelector('source')) load(this.dataset.autoplay !== 'false'); }
      else player.pause();
    }, { root, rootMargin: `${root?.clientHeight || window.innerHeight}px 0px`, threshold: 0 });
    this.#observer.observe(this);
  }
}
if (!customElements.get('gb-pdp-training')) customElements.define('gb-pdp-training', GbPdpTraining);
